import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

import { cn, focusOnPointerDown } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        // dark:bg-destructive/60 removed: it re-introduced alpha and landed
        // near 3.1:1 against its own foreground.
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        // secondary sits near the 3:1 floor, so the /80 alpha hover would drop
        // under it; the other variants keep theirs.
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary-text underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/** Sizes that render an icon with no visible text, so they need an explicit name. */
type IconSize = "icon" | "icon-xs" | "icon-sm" | "icon-lg";
type TextSize = "default" | "xs" | "sm" | "lg";

type BaseButtonProps = React.ComponentProps<"button"> &
  Omit<VariantProps<typeof buttonVariants>, "size"> & {
    asChild?: boolean;
  };

/**
 * Icon-only buttons must carry an accessible name. Lucide icons render as
 * `aria-hidden` SVGs, so without this a screen reader announces only"button"—
 * which is what most of this toolbar surface used to do. Making it a type error
 * is what stops the defect coming back.
 */
export type ButtonProps = BaseButtonProps &
  (
    | { size?: TextSize }
    | ({ size: IconSize } & (
        | { "aria-label": string }
        | { "aria-labelledby": string }
      ))
  );

/**
 * `Omit` distributes over the union instead of collapsing it to the common
 * keys, so wrappers around Button keep the size/accessible-name pairing.
 */
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  type,
  onPointerDown,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      // Normalises Safari and Firefox on macOS, which leave focus on <body>
      // when a button is clicked — so any overlay opened from here would hand
      // focus back to the top of the page instead of to this button.
      onPointerDown={(event: React.PointerEvent<HTMLElement>) => {
        focusOnPointerDown(event);
        onPointerDown?.(event as React.PointerEvent<HTMLButtonElement>);
      }}
      data-variant={variant}
      data-size={size}
      // Without this every button defaults to type="submit". Left alone when
      // asChild, where the rendered element may not be a button at all.
      type={asChild ? type : (type ?? "button")}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
